<template>
  <div class="food-admin-shell">
    <section class="food-hero">
      <div>
        <p class="eyebrow">LotLedger Wine Bar</p>
        <h1>Food Inventory</h1>
        <p>
          Manage plate ingredients, receive stock, adjust counts, and track moving average cost.
        </p>
      </div>

      <button class="primary-action" @click="openIngredientModal()">
        + Add Ingredient
      </button>
    </section>

    <section class="summary-grid">
      <article class="summary-card">
        <span>Total Ingredients</span>
        <strong>{{ foodIngredients.length }}</strong>
      </article>

      <article class="summary-card">
        <span>Inventory Value</span>
        <strong>${{ money(totalFoodValue) }}</strong>
      </article>

      <article class="summary-card warning">
        <span>Below Par</span>
        <strong>{{ belowParCount }}</strong>
      </article>
    </section>

    <section class="toolbar-card">
      <label>
        <span>Search</span>
        <input
          type="text"
          v-model="searchText"
          placeholder="Search ingredients..."
        />
      </label>

      <label>
        <span>Category</span>
        <select v-model="categoryFilter">
          <option value="">All Categories</option>
          <option
            v-for="category in categories"
            :key="category"
            :value="category"
          >
            {{ category }}
          </option>
        </select>
      </label>

      <button class="ghost-action" @click="fetchFoodIngredients">
        Refresh
      </button>
    </section>

    <section class="ingredient-grid">
      <article
        v-for="ingredient in filteredIngredients"
        :key="ingredient.ingredient_id"
        class="ingredient-card"
        :class="{ low: isBelowPar(ingredient) }"
      >
        <div class="ingredient-top">
          <div>
            <p class="label">{{ ingredient.category || 'Ingredient' }}</p>
            <h2>{{ ingredient.ingredient_name }}</h2>
          </div>

          <span v-if="isBelowPar(ingredient)" class="low-pill">
            Below Par
          </span>
        </div>

        <div class="metric-grid">
          <div>
            <span>On Hand</span>
            <strong>
              {{ number(ingredient.quantity_on_hand) }} {{ ingredient.inventory_unit }}
            </strong>
          </div>

          <div>
            <span>MAC</span>
            <strong>
              ${{ money(ingredient.moving_avg_cost) }} / {{ ingredient.inventory_unit }}
            </strong>
          </div>

          <div>
            <span>Value</span>
            <strong>
              ${{ money(ingredient.inventory_value) }}
            </strong>
          </div>

          <div>
            <span>Par</span>
            <strong>
              {{ number(ingredient.par_level) }} {{ ingredient.inventory_unit }}
            </strong>
          </div>
        </div>

        <p v-if="ingredient.preferred_vendor" class="ingredient-note">
          Vendor: {{ ingredient.preferred_vendor }}
        </p>

        <p v-if="ingredient.notes" class="ingredient-note">
          {{ ingredient.notes }}
        </p>

        <div class="ingredient-actions">
          <button class="primary-action small" @click="openReceiveModal(ingredient)">
            Receive
          </button>

          <button class="ghost-action small" @click="openAdjustModal(ingredient)">
            Adjust
          </button>

          <button class="ghost-action small" @click="openIngredientModal(ingredient)">
            Edit
          </button>
        </div>
      </article>
    </section>

    <div v-if="!filteredIngredients.length" class="empty-state">
      <h3>No food ingredients found</h3>
      <p>Add ingredients like Baguette, Cheddar Cheese, Salami, Olives, and Pistachios.</p>
    </div>

    <!-- INGREDIENT MODAL -->
    <div v-if="showIngredientModal" class="modal-overlay">
      <div class="food-modal">
        <div class="modal-topline"></div>

        <header class="modal-header">
          <div>
            <p class="eyebrow">{{ editingIngredient?.ingredient_id ? 'Edit' : 'New' }} Ingredient</p>
            <h2>{{ editingIngredient?.ingredient_id ? 'Edit Ingredient' : 'Add Ingredient' }}</h2>
            <p>Use ounces for most food ingredients and eaches for baguettes.</p>
          </div>

          <button class="icon-close" @click="closeIngredientModal">×</button>
        </header>

        <div class="modal-body">
          <section class="form-grid">
            <label>
              <span>Name</span>
              <input v-model="ingredientForm.ingredient_name" type="text" />
            </label>

            <label>
              <span>Category</span>
              <input v-model="ingredientForm.category" type="text" placeholder="Bread, Cheese, Meat..." />
            </label>

            <label>
              <span>Inventory Unit</span>
              <select v-model="ingredientForm.inventory_unit">
                <option value="oz">oz</option>
                <option value="each">each</option>
                <option value="lb">lb</option>
                <option value="box">box</option>
                <option value="jar">jar</option>
              </select>
            </label>

            <label>
              <span>Serving Unit</span>
              <select v-model="ingredientForm.serving_unit">
                <option value="oz">oz</option>
                <option value="each">each</option>
                <option value="lb">lb</option>
                <option value="box">box</option>
                <option value="jar">jar</option>
              </select>
            </label>

            <label>
              <span>Conversion</span>
              <input
                v-model.number="ingredientForm.conversion_to_inventory"
                type="number"
                step="0.000001"
                min="0"
              />
            </label>

            <label>
              <span>Quantity On Hand</span>
              <input
                v-model.number="ingredientForm.quantity_on_hand"
                type="number"
                step="0.0001"
                min="0"
              />
            </label>

            <label>
              <span>Moving Avg Cost</span>
              <input
                v-model.number="ingredientForm.moving_avg_cost"
                type="number"
                step="0.0001"
                min="0"
              />
            </label>

            <label>
              <span>Par Level</span>
              <input
                v-model.number="ingredientForm.par_level"
                type="number"
                step="0.0001"
                min="0"
              />
            </label>

            <label>
              <span>Preferred Vendor</span>
              <input v-model="ingredientForm.preferred_vendor" type="text" />
            </label>

            <label class="full-width">
              <span>Notes</span>
              <input v-model="ingredientForm.notes" type="text" />
            </label>
          </section>

          <footer class="modal-actions">
            <button class="cancel-action" @click="closeIngredientModal">
              Cancel
            </button>

            <button class="primary-action" @click="saveIngredient">
              Save Ingredient
            </button>
          </footer>
        </div>
      </div>
    </div>

    <!-- RECEIVE MODAL -->
    <div v-if="showReceiveModal" class="modal-overlay">
      <div class="food-modal small-modal">
        <div class="modal-topline gold"></div>

        <header class="modal-header">
          <div>
            <p class="eyebrow">Receive Food</p>
            <h2>{{ selectedIngredient?.ingredient_name }}</h2>
            <p>
              Enter received quantity in {{ selectedIngredient?.inventory_unit }} and total invoice cost.
            </p>
          </div>

          <button class="icon-close" @click="closeReceiveModal">×</button>
        </header>

        <div class="modal-body">
          <section class="form-grid">
            <label>
              <span>Quantity Received</span>
              <input
                v-model.number="receiveForm.quantity_received"
                type="number"
                step="0.0001"
                min="0"
              />
            </label>

            <label>
              <span>Total Cost</span>
              <input
                v-model.number="receiveForm.total_cost"
                type="number"
                step="0.01"
                min="0"
              />
            </label>

            <label class="full-width">
              <span>Notes</span>
              <input v-model="receiveForm.notes" type="text" />
            </label>
          </section>

          <div class="calc-box">
            <span>Received Unit Cost</span>
            <strong>
              ${{ money(receivedUnitCost) }} / {{ selectedIngredient?.inventory_unit }}
            </strong>
          </div>

          <footer class="modal-actions">
            <button class="cancel-action" @click="closeReceiveModal">
              Cancel
            </button>

            <button class="primary-action" @click="receiveIngredient">
              Receive Inventory
            </button>
          </footer>
        </div>
      </div>
    </div>

    <!-- ADJUST MODAL -->
    <div v-if="showAdjustModal" class="modal-overlay">
      <div class="food-modal small-modal">
        <div class="modal-topline amber"></div>

        <header class="modal-header">
          <div>
            <p class="eyebrow">Physical Count</p>
            <h2>{{ selectedIngredient?.ingredient_name }}</h2>
            <p>Set the final on-hand quantity after count, spoilage, or correction.</p>
          </div>

          <button class="icon-close" @click="closeAdjustModal">×</button>
        </header>

        <div class="modal-body">
          <section class="form-grid">
            <label>
              <span>New Quantity On Hand</span>
              <input
                v-model.number="adjustForm.new_quantity_on_hand"
                type="number"
                step="0.0001"
                min="0"
              />
            </label>

            <label class="full-width">
              <span>Notes</span>
              <input v-model="adjustForm.notes" type="text" />
            </label>
          </section>

          <footer class="modal-actions">
            <button class="cancel-action" @click="closeAdjustModal">
              Cancel
            </button>

            <button class="primary-action" @click="adjustIngredient">
              Save Count
            </button>
          </footer>
        </div>
      </div>
    </div>

    <!-- WARNING MODAL -->
    <div v-if="showWarning" class="modal-overlay">
      <div class="food-modal small-modal">
        <div class="modal-topline amber"></div>

        <header class="modal-header">
          <div>
            <p class="eyebrow">Heads Up</p>
            <h2>Check This</h2>
            <p>{{ warningMessage }}</p>
          </div>

          <button class="icon-close" @click="showWarning = false">×</button>
        </header>

        <div class="modal-body">
          <button class="primary-action" @click="showWarning = false">
            OK
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'FoodAdminPage',

  data() {
    return {
      foodIngredients: [],
      searchText: '',
      categoryFilter: '',

      showIngredientModal: false,
      editingIngredient: null,

      ingredientForm: this.emptyIngredientForm(),

      showReceiveModal: false,
      selectedIngredient: null,
      receiveForm: {
        quantity_received: 0,
        total_cost: 0,
        notes: ''
      },

      showAdjustModal: false,
      adjustForm: {
        new_quantity_on_hand: 0,
        notes: ''
      },

      showWarning: false,
      warningMessage: ''
    };
  },

  computed: {
    categories() {
      return [...new Set(
        this.foodIngredients
          .map(item => item.category)
          .filter(Boolean)
      )].sort();
    },

    filteredIngredients() {
      const search = this.searchText.toLowerCase();

      return this.foodIngredients.filter(item => {
        const matchesSearch =
          !search ||
          String(item.ingredient_name || '').toLowerCase().includes(search) ||
          String(item.category || '').toLowerCase().includes(search) ||
          String(item.preferred_vendor || '').toLowerCase().includes(search);

        const matchesCategory =
          !this.categoryFilter ||
          item.category === this.categoryFilter;

        return matchesSearch && matchesCategory;
      });
    },

    totalFoodValue() {
      return this.foodIngredients.reduce((sum, item) => {
        return sum + Number(item.inventory_value || 0);
      }, 0);
    },

    belowParCount() {
      return this.foodIngredients.filter(item => this.isBelowPar(item)).length;
    },

    receivedUnitCost() {
      const qty = Number(this.receiveForm.quantity_received || 0);
      const total = Number(this.receiveForm.total_cost || 0);

      return qty > 0 ? total / qty : 0;
    }
  },

  mounted() {
    this.fetchFoodIngredients();
  },

  methods: {
    emptyIngredientForm() {
      return {
        ingredient_name: '',
        category: '',
        inventory_unit: 'oz',
        serving_unit: 'oz',
        conversion_to_inventory: 1,
        quantity_on_hand: 0,
        moving_avg_cost: 0,
        preferred_vendor: '',
        par_level: 0,
        notes: '',
        active: true
      };
    },

    async fetchFoodIngredients() {
      try {
        const response = await api.get('/food-ingredients');
        this.foodIngredients = response.data || [];
      } catch (error) {
        console.error('❌ Error fetching food ingredients:', error);
        this.foodIngredients = [];
        this.showWarningPopup('Unable to load food inventory.');
      }
    },

    openIngredientModal(ingredient = null) {
      this.editingIngredient = ingredient;

      if (ingredient) {
        this.ingredientForm = {
          ingredient_name: ingredient.ingredient_name || '',
          category: ingredient.category || '',
          inventory_unit: ingredient.inventory_unit || 'oz',
          serving_unit: ingredient.serving_unit || 'oz',
          conversion_to_inventory: Number(ingredient.conversion_to_inventory || 1),
          quantity_on_hand: Number(ingredient.quantity_on_hand || 0),
          moving_avg_cost: Number(ingredient.moving_avg_cost || 0),
          preferred_vendor: ingredient.preferred_vendor || '',
          par_level: Number(ingredient.par_level || 0),
          notes: ingredient.notes || '',
          active: ingredient.active !== false
        };
      } else {
        this.ingredientForm = this.emptyIngredientForm();
      }

      this.showIngredientModal = true;
    },

    closeIngredientModal() {
      this.showIngredientModal = false;
      this.editingIngredient = null;
      this.ingredientForm = this.emptyIngredientForm();
    },

    async saveIngredient() {
      if (!this.ingredientForm.ingredient_name.trim()) {
        this.showWarningPopup('Ingredient name is required.');
        return;
      }

      try {
        if (this.editingIngredient?.ingredient_id) {
          await api.put(`/food-ingredients/${this.editingIngredient.ingredient_id}`, this.ingredientForm);
        } else {
          await api.post('/food-ingredients', this.ingredientForm);
        }

        this.closeIngredientModal();
        await this.fetchFoodIngredients();
      } catch (error) {
        console.error('❌ Error saving ingredient:', error);
        this.showWarningPopup(
          error.response?.data?.details ||
          error.response?.data?.error ||
          'Unable to save ingredient.'
        );
      }
    },

    openReceiveModal(ingredient) {
      this.selectedIngredient = ingredient;
      this.receiveForm = {
        quantity_received: 0,
        total_cost: 0,
        notes: ''
      };
      this.showReceiveModal = true;
    },

    closeReceiveModal() {
      this.showReceiveModal = false;
      this.selectedIngredient = null;
      this.receiveForm = {
        quantity_received: 0,
        total_cost: 0,
        notes: ''
      };
    },

    async receiveIngredient() {
      if (!this.selectedIngredient?.ingredient_id) {
        this.showWarningPopup('No ingredient selected.');
        return;
      }

      if (Number(this.receiveForm.quantity_received || 0) <= 0) {
        this.showWarningPopup('Quantity received must be greater than zero.');
        return;
      }

      try {
        await api.post(`/food-ingredients/${this.selectedIngredient.ingredient_id}/receive`, this.receiveForm);

        this.closeReceiveModal();
        await this.fetchFoodIngredients();
      } catch (error) {
        console.error('❌ Error receiving ingredient:', error);
        this.showWarningPopup(
          error.response?.data?.details ||
          error.response?.data?.error ||
          'Unable to receive ingredient.'
        );
      }
    },

    openAdjustModal(ingredient) {
      this.selectedIngredient = ingredient;
      this.adjustForm = {
        new_quantity_on_hand: Number(ingredient.quantity_on_hand || 0),
        notes: ''
      };
      this.showAdjustModal = true;
    },

    closeAdjustModal() {
      this.showAdjustModal = false;
      this.selectedIngredient = null;
      this.adjustForm = {
        new_quantity_on_hand: 0,
        notes: ''
      };
    },

    async adjustIngredient() {
      if (!this.selectedIngredient?.ingredient_id) {
        this.showWarningPopup('No ingredient selected.');
        return;
      }

      try {
        await api.post(`/food-ingredients/${this.selectedIngredient.ingredient_id}/adjust`, this.adjustForm);

        this.closeAdjustModal();
        await this.fetchFoodIngredients();
      } catch (error) {
        console.error('❌ Error adjusting ingredient:', error);
        this.showWarningPopup(
          error.response?.data?.details ||
          error.response?.data?.error ||
          'Unable to adjust ingredient.'
        );
      }
    },

    isBelowPar(item) {
      return Number(item.par_level || 0) > 0 &&
        Number(item.quantity_on_hand || 0) < Number(item.par_level || 0);
    },

    money(value) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(Number(value || 0));
    },

    number(value) {
      return Number(value || 0).toLocaleString('en-US', {
        maximumFractionDigits: 4
      });
    },

    showWarningPopup(message) {
      this.warningMessage = message;
      this.showWarning = true;
    }
  }
};
</script>

<style scoped>
.food-admin-shell {
  min-height: 100vh;
  padding: 28px;
  color: #fff8f1;
  font-family: "Poppins", sans-serif;
  background:
    radial-gradient(circle at top left, rgba(128, 18, 54, 0.28), transparent 34%),
    linear-gradient(135deg, #120b10, #22131a 48%, #0f0a0d);
}

.food-hero {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  align-items: flex-end;
  padding: 26px;
  margin-bottom: 18px;
  border-radius: 30px;
  background:
    linear-gradient(135deg, rgba(255,255,255,0.09), rgba(255,255,255,0.035));
  border: 1px solid rgba(255,255,255,0.11);
  box-shadow: 0 30px 80px rgba(0,0,0,0.35);
}

.eyebrow,
.label {
  margin: 0 0 8px;
  color: #c8a66a;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.food-hero h1 {
  margin: 0;
  font-size: clamp(42px, 6vw, 82px);
  letter-spacing: -0.08em;
  line-height: 0.9;
}

.food-hero p {
  max-width: 650px;
  margin: 14px 0 0;
  color: #d8cbc4;
  line-height: 1.5;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(160px, 1fr));
  gap: 14px;
  margin-bottom: 16px;
}

.summary-card,
.toolbar-card,
.ingredient-card {
  border-radius: 24px;
  background: rgba(255,255,255,0.065);
  border: 1px solid rgba(255,255,255,0.095);
  box-shadow: 0 20px 55px rgba(0,0,0,0.22);
}

.summary-card {
  padding: 20px;
}

.summary-card span {
  display: block;
  color: #b9aaa4;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.summary-card strong {
  display: block;
  margin-top: 8px;
  font-size: 34px;
  letter-spacing: -0.05em;
}

.summary-card.warning strong {
  color: #f1c979;
}

.toolbar-card {
  display: grid;
  grid-template-columns: 1fr 240px auto;
  gap: 14px;
  align-items: end;
  padding: 18px;
  margin-bottom: 18px;
}

.toolbar-card label,
.form-grid label {
  display: grid;
  gap: 8px;
}

.toolbar-card label span,
.form-grid label span {
  color: #b9aaa4;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

input,
select {
  min-height: 44px;
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 14px;
  background: rgba(8, 5, 8, 0.58);
  color: #fff8f1;
  padding: 0 14px;
  outline: none;
}

.ingredient-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(310px, 1fr));
  gap: 16px;
}

.ingredient-card {
  padding: 18px;
}

.ingredient-card.low {
  border-color: rgba(241, 201, 121, 0.45);
}

.ingredient-top {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
}

.ingredient-top h2 {
  margin: 0;
  font-size: 28px;
  letter-spacing: -0.05em;
}

.low-pill {
  padding: 7px 10px;
  border-radius: 999px;
  color: #171016;
  background: #f1c979;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-top: 16px;
}

.metric-grid div {
  padding: 12px;
  border-radius: 16px;
  background: rgba(0,0,0,0.22);
}

.metric-grid span {
  display: block;
  color: #9b8f8b;
  font-size: 10px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.metric-grid strong {
  display: block;
  margin-top: 5px;
  font-size: 15px;
}

.ingredient-note {
  margin: 12px 0 0;
  color: #cfc2bd;
  line-height: 1.4;
}

.ingredient-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
}

.primary-action,
.ghost-action,
.cancel-action {
  min-height: 44px;
  padding: 0 16px;
  border-radius: 999px;
  border: 0;
  cursor: pointer;
  font-weight: 900;
}

.primary-action {
  color: #1b1015;
  background: linear-gradient(135deg, #f1c979, #c8a66a);
}

.ghost-action {
  color: #fff8f1;
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.12);
}

.cancel-action {
  color: #fff8f1;
  background: rgba(255,255,255,0.08);
}

.small {
  min-height: 36px;
  padding: 0 12px;
  font-size: 12px;
}

.empty-state {
  margin-top: 22px;
  padding: 26px;
  border-radius: 24px;
  text-align: center;
  background: rgba(255,255,255,0.06);
}

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 26px;
  overflow: hidden;
  background:
    radial-gradient(circle at top left, rgba(128, 18, 54, 0.34), transparent 36%),
    rgba(5, 3, 5, 0.78);
  backdrop-filter: blur(12px);
}

.food-modal {
  width: min(720px, 94vw);
  max-height: calc(100vh - 52px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-radius: 30px;
  background:
    linear-gradient(145deg, rgba(35, 22, 29, 0.98), rgba(14, 10, 14, 0.98));
  border: 1px solid rgba(255,255,255,0.11);
  color: #f7efe8;
  box-shadow: 0 34px 90px rgba(0,0,0,0.55);
}

.small-modal {
  width: min(560px, 94vw);
}

.modal-topline {
  flex: 0 0 auto;
  height: 5px;
  background: linear-gradient(90deg, #8f123f, #c24a2f, #c8a66a);
}

.modal-topline.gold {
  background: linear-gradient(90deg, #c8a66a, #f1c979);
}

.modal-topline.amber {
  background: linear-gradient(90deg, #c8a66a, #d77a2d);
}

.modal-header {
  flex: 0 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 18px;
  padding: 26px 28px 18px;
  border-bottom: 1px solid rgba(255,255,255,0.08);
}

.modal-header h2 {
  margin: 0;
  font-size: 36px;
  line-height: 0.95;
  letter-spacing: -0.06em;
}

.modal-header p {
  margin: 10px 0 0;
  color: #cfc2bd;
  line-height: 1.45;
}

.icon-close {
  width: 46px;
  height: 46px;
  flex: 0 0 46px;
  border-radius: 999px;
  border: 1px solid rgba(255,255,255,0.12);
  background: rgba(255,255,255,0.07);
  color: #fff8f1;
  font-size: 30px;
  cursor: pointer;
}

.modal-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 24px 28px 28px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(180px, 1fr));
  gap: 14px;
}

.full-width {
  grid-column: 1 / -1;
}

.calc-box {
  margin-top: 16px;
  padding: 16px;
  border-radius: 18px;
  background: rgba(0,0,0,0.24);
}

.calc-box span {
  display: block;
  color: #b9aaa4;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
}

.calc-box strong {
  display: block;
  margin-top: 6px;
  font-size: 24px;
  color: #f1c979;
}

.modal-actions {
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin: 20px -28px -28px;
  padding: 18px 28px 24px;
  background:
    linear-gradient(
      180deg,
      rgba(14,10,14,0),
      rgba(14,10,14,0.94) 30%,
      rgba(14,10,14,1)
    );
}

@media (max-width: 760px) {
  .food-admin-shell {
    padding: 14px;
  }

  .food-hero,
  .toolbar-card {
    grid-template-columns: 1fr;
    flex-direction: column;
    align-items: stretch;
  }

  .summary-grid {
    grid-template-columns: 1fr;
  }

  .toolbar-card,
  .form-grid {
    grid-template-columns: 1fr;
  }

  .modal-overlay {
    align-items: stretch;
    padding: 12px;
  }

  .food-modal {
    width: 100%;
    max-height: calc(100vh - 24px);
  }
}
</style>